import React, { useEffect, useRef, useState } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import { ProvisionStepsPanel } from "../components/provision-steps-panel";
import { DomainPanel } from "../components/domain-panel";
import { QuotasPanel } from "../components/quotas-panel";
import { SchemaStatus } from "../components/schema-status";
import { Badge } from "@pgs-core/ui/components/badge";
import { Button } from "@pgs-core/ui/components/button";
import { Input } from "@pgs-core/ui/components/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@pgs-core/ui/components/select";
import { ExternalLink, Loader2, Pencil } from "lucide-react";
import { deriveBuildState, buildInProgress, type BuildState } from "../lib/build";
import type { ApiClient } from "@pgs-core/portal-lib/api";
import type { AdminClient, AdminOutletContext } from "../lib/outlet-context";
import { PLAN_LABELS } from "../lib/plans";
import { formatTimestamp } from "@pgs-core/portal-lib/format";

const CF_ACCOUNT_ID = "b6298701e808f2098dae865298b26637";

interface CfResource {
  key: string;
  label: string;
  provisioned: (client: AdminClient) => boolean;
  href: (client: AdminClient) => string;
}

const CF_RESOURCES: CfResource[] = [
  {
    key: "d1",
    label: "D1",
    provisioned: (c: AdminClient) => Boolean(c.d1_database_id),
    href: (c: AdminClient) => `https://dash.cloudflare.com/?to=/${CF_ACCOUNT_ID}/workers/d1/databases/${c.d1_database_id}`,
  },
  {
    key: "r2",
    label: "R2",
    provisioned: (c: AdminClient) => Boolean(c.assets_bucket_name),
    href: (c: AdminClient) => `https://dash.cloudflare.com/?to=/${CF_ACCOUNT_ID}/r2/default/buckets/${c.assets_bucket_name}`,
  },
  {
    key: "pages",
    label: "Pages",
    provisioned: (c: AdminClient) => Boolean(c.pages_project_name),
    href: (c: AdminClient) => `https://dash.cloudflare.com/?to=/${CF_ACCOUNT_ID}/pages/view/${c.pages_project_name}`,
  },
];

// Per-resource status dot. "error" only applies to Pages (a failed build);
// D1/R2 have no error signal in the admin row, so they're green/grey only.
const STATUS_DOT: Record<"ok" | "none" | "error", { className: string; title: string }> = {
  ok: { className: "bg-success", title: "Provisioned" },
  none: { className: "bg-muted-foreground/30", title: "Not provisioned" },
  error: { className: "bg-destructive", title: "Error" },
};

function resourceStatus(resource: CfResource, client: AdminClient, build: BuildState | null): "ok" | "none" | "error" {
  if (resource.key === "pages" && build?.state === "failed") return "error";
  return resource.provisioned(client) ? "ok" : "none";
}

const PLAN_OPTIONS = Object.entries(PLAN_LABELS);

// Client details card with inline editing of business name + plan. Read-only by
// default; the pencil reveals a form that PATCHes /admin/clients/:id and pushes
// the updated row back into shared client state via updateClient.
function ClientDetailsCard({ client, build, api, updateClient }: {
  client: AdminClient;
  build: BuildState;
  api: ApiClient | null;
  updateClient: (client: AdminClient) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [businessName, setBusinessName] = useState(client.business_name ?? "");
  const [plan, setPlan] = useState(client.plan ?? "launch");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const startEditing = () => {
    setBusinessName(client.business_name ?? "");
    setPlan(client.plan ?? "launch");
    setError("");
    setEditing(true);
  };

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!api) {
      setError("No API configured.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const result = await api.request<{ client?: AdminClient }>(`/admin/clients/${client.id}`, {
        method: "PATCH",
        body: JSON.stringify({ business_name: businessName.trim(), plan }),
      });
      if (result?.client) updateClient(result.client);
      setEditing(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Client details
        </p>
        {!editing && (
          <button
            type="button"
            onClick={startEditing}
            className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            <Pencil className="h-3 w-3" /> Edit
          </button>
        )}
      </div>

      {editing ? (
        <form onSubmit={handleSave} className="space-y-3 text-sm">
          <div className="space-y-1.5">
            <label htmlFor="businessName" className="text-muted-foreground">Business name</label>
            <Input
              id="businessName"
              value={businessName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBusinessName(e.target.value)}
              placeholder="Barrow Cafe"
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="plan" className="text-muted-foreground">Plan</label>
            <Select value={plan} onValueChange={setPlan}>
              <SelectTrigger id="plan">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PLAN_OPTIONS.map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {error && <p className="text-destructive">{error}</p>}
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={saving}>
              {saving && <Loader2 className="h-3 w-3 animate-spin" />}
              {saving ? "Saving…" : "Save"}
            </Button>
            <Button type="button" size="sm" variant="ghost" disabled={saving} onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
          <dt className="text-muted-foreground">Business name</dt>
          <dd className="font-medium">{client.business_name ?? <span className="text-muted-foreground italic">—</span>}</dd>
          <dt className="text-muted-foreground">Tenant ID</dt>
          <dd className="font-mono font-medium">{client.id}</dd>
          <dt className="text-muted-foreground">Plan</dt>
          <dd>
            <Badge variant="secondary">{(client.plan && PLAN_LABELS[client.plan]) || client.plan}</Badge>
          </dd>
          <dt className="text-muted-foreground">Status</dt>
          <dd>
            <Badge variant={client.lifecycle === "Active" ? "success" : client.lifecycle === "Provisioning" ? "warning" : "secondary"}>
              {client.lifecycle}
            </Badge>
          </dd>
          <dt className="text-muted-foreground">Site build</dt>
          <dd className="flex items-center gap-2">
            <Badge variant={build.badgeVariant} className="gap-1">
              {build.inProgress && <Loader2 className="h-3 w-3 animate-spin" />}
              {build.label}
            </Badge>
            {build.at && (
              <span className="text-xs text-muted-foreground">{formatTimestamp(build.at, "")}</span>
            )}
          </dd>
        </dl>
      )}
    </div>
  );
}

export function ClientOverviewPage() {
  const { clientId } = useParams();
  const { clients, updateClient, api } = useOutletContext<AdminOutletContext>();

  const client = clients.find((c) => c.id === clientId);

  // While this client's build is running, poll the (cheap) client endpoint so
  // the indicator updates itself. Single-flight, with a hard stop.
  const building = buildInProgress(client);
  const pollingRef = useRef(false);
  useEffect(() => {
    if (!api || !client || !building) return;

    let cancelled = false;
    const deadline = Date.now() + 5 * 60 * 1000;
    const interval = setInterval(async () => {
      if (pollingRef.current) return;
      if (Date.now() > deadline) {
        clearInterval(interval);
        return;
      }
      pollingRef.current = true;
      try {
        const body = await api.request<{ client?: AdminClient }>(`/admin/clients/${client.id}`);
        if (!cancelled && body?.client) updateClient(body.client);
      } catch {
        // Transient — keep polling until the deadline.
      } finally {
        pollingRef.current = false;
      }
    }, 6000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [api, client?.id, building, updateClient]);

  if (!client) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
        <p className="text-sm text-muted-foreground">Client not found</p>
        <p className="mt-1 text-xs text-muted-foreground">
          It may still be loading — try refreshing.
        </p>
      </div>
    );
  }

  const build = deriveBuildState(client);

  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Client details */}
        <ClientDetailsCard
          client={client}
          build={build}
          api={api}
          updateClient={updateClient}
        />

        {/* Quotas */}
        <QuotasPanel client={client} api={api} />

        {/* Infrastructure */}
        <div className="rounded-lg border bg-card p-4">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Infrastructure
          </p>

          <dl className="mb-4 grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Internal</dt>
            <dd className="min-w-0">
              {client.pages_project_name ? (
                <a
                  href={`https://${client.pages_project_name}.pages.dev`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex min-w-0 items-center gap-1 font-medium text-primary hover:underline"
                >
                  <span className="truncate">{client.pages_project_name}.pages.dev</span>
                  <ExternalLink className="h-3 w-3 shrink-0" />
                </a>
              ) : (
                <span className="text-muted-foreground italic">Not provisioned</span>
              )}
            </dd>
            <dt className="text-muted-foreground">Custom domain</dt>
            <dd className="min-w-0">
              {client.custom_domain ? (
                <a
                  href={`https://${client.custom_domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex min-w-0 items-center gap-1 font-medium text-primary hover:underline"
                >
                  <span className="truncate">{client.custom_domain}</span>
                  <ExternalLink className="h-3 w-3 shrink-0" />
                </a>
              ) : (
                <span className="text-muted-foreground italic">Not mapped</span>
              )}
            </dd>
          </dl>

          <div className="grid grid-cols-3 gap-3">
            {CF_RESOURCES.map((resource) => {
              const status = STATUS_DOT[resourceStatus(resource, client, build)];
              const provisioned = resource.provisioned(client);
              return (
                <div
                  key={resource.key}
                  className="flex items-center justify-between rounded-md border bg-background p-3"
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`h-2 w-2 shrink-0 rounded-full ${status.className}`}
                      title={status.title}
                    />
                    <span className="text-sm font-medium">{resource.label}</span>
                  </div>
                  {provisioned ? (
                    <a
                      href={resource.href(client)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground transition-colors hover:text-foreground"
                      title={`Open ${resource.label} in Cloudflare`}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  ) : (
                    <ExternalLink className="h-4 w-4 text-muted-foreground/30" />
                  )}
                </div>
              );
            })}
          </div>

          <SchemaStatus
            client={client}
            api={api}
            onMigrated={updateClient}
          />
        </div>
      </div>

      <ProvisionStepsPanel
        client={client}
        api={api}
        onClientUpdated={updateClient}
      />

      {/* Custom-domain lifecycle (replaces the old domain provisioning step);
          renders once the Pages project exists. */}
      <DomainPanel
        client={client}
        api={api}
        onClientUpdated={updateClient}
      />
    </div>
  );
}
